/**
 * `/licenses/:id` (OCP-299) — single self-host license detail.
 *
 * Metadata + linked-instances table (which orboto-core deployments are
 * checking in against this license, and their status) + revoke. The
 * list/mint/rotate surface lives in AdminLicensesPage.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { AgentLanesView } from '@orboto/ocp-shared';

import { AgentLanesField } from '../components/AgentLanesField';
import { api, ApiError, readErrorKey } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type Tier = 'solo' | 'team' | 'business' | 'enterprise';
type LicenseStatus = 'active' | 'revoked' | 'expired';
type InstanceStatus = 'active' | 'stale' | 'blocked';

interface License {
  id: string;
  display_name: string;
  tier: Tier;
  status: LicenseStatus;
  max_instances: number | null;
  features: string[];
  air_gapped: boolean;
  issued_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  license_key_prefix: string;
  subscription_id: string | null;
  /** OCP-985 - hosted agent lanes the license pays for (signed into the JWT claim agentLanes). */
  agent_lanes?: AgentLanesView;
}

interface EntitlementHalf {
  status: 'active' | 'revoked' | 'unavailable';
  key_prefix: string | null;
  from?: string | null;
  base_url: string;
  minted_at: string | null;
  delivered_at: string | null;
  revoked_at: string | null;
}

/** OCP-586 - the included mail + AI credentials one instance received. */
interface EntitlementRow {
  id: string;
  instance_id: string;
  instance_url: string | null;
  mail: EntitlementHalf;
  ai: EntitlementHalf;
  rotations: number;
  revoked_reason: string | null;
  last_activated_at: string;
}

interface InstanceRow {
  id: string;
  instance_id: string;
  instance_url: string | null;
  status: InstanceStatus;
  first_seen_at: string;
  last_seen_at: string;
  last_verify_meta: Record<string, unknown> | null;
}

export function AdminLicenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [license, setLicense] = useState<License | null>(null);
  const [instances, setInstances] = useState<InstanceRow[]>([]);
  const [entitlements, setEntitlements] = useState<EntitlementRow[]>([]);
  const [entBusy, setEntBusy] = useState<string | null>(null);
  const [entFlash, setEntFlash] = useState<string | null>(null);
  const [rotated, setRotated] = useState<{ mail: string | null; ai: string | null } | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const [lic, inst, ents] = await Promise.all([
        api<License>(`/v1/admin/licenses/${id}`),
        api<{ items: InstanceRow[] }>(`/v1/admin/licenses/${id}/instances`),
        api<{ items: EntitlementRow[] }>(`/v1/admin/licenses/${id}/entitlements`).catch(() => ({ items: [] as EntitlementRow[] })),
      ]);
      setLicense(lic);
      setInstances(inst.items);
      setEntitlements(ents.items);
    } catch (err) {
      if (err instanceof ApiError)
        setError({
          status: err.status,
          message: err.status === 404 ? 'License not found.' : `Load failed (HTTP ${err.status}).`,
        });
      else setError({ status: 0, message: 'Load failed.' });
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const revokeEntitlement = async (e: EntitlementRow) => {
    const reason = window.prompt(`Revoke the included mail + AI credentials of instance ${e.instance_id}? Reason (audit):`);
    if (!reason || !reason.trim() || !id) return;
    setEntBusy(e.id);
    setEntFlash(null);
    try {
      await api(`/v1/admin/licenses/${id}/entitlements/${e.id}/revoke`, { method: 'POST', body: { reason: reason.trim() } });
      setEntFlash(`Credentials of ${e.instance_id} revoked.`);
      await load();
    } catch (err) {
      setEntFlash(err instanceof ApiError ? `Revoke failed (HTTP ${err.status}).` : 'Revoke failed.');
    } finally {
      setEntBusy(null);
    }
  };

  const rotateEntitlement = async (e: EntitlementRow, scope: 'mail' | 'ai' | 'all') => {
    const reason = window.prompt(`Rotate the ${scope === 'all' ? 'mail + AI' : scope} credentials of instance ${e.instance_id}? The old keys stop working now. Reason (audit):`);
    if (!reason || !reason.trim() || !id) return;
    setEntBusy(e.id);
    setEntFlash(null);
    setRotated(null);
    try {
      const r = await api<{ mail: { api_key: string | null }; ai: { api_key: string | null } }>(`/v1/admin/licenses/${id}/entitlements/${e.id}/rotate`, { method: 'POST', body: { reason: reason.trim(), scope } });
      setRotated({ mail: r.mail.api_key, ai: r.ai.api_key });
      setEntFlash(`Credentials of ${e.instance_id} rotated - copy the new material now, it is shown once.`);
      await load();
    } catch (err) {
      // OCP-932 - 503 activation_in_progress: an activation / rotation of this instance is in flight; 409 = inactive license.
      setEntFlash(
        err instanceof ApiError
          ? readErrorKey(err.body) === 'activation_in_progress'
            ? 'The instance is activating or rotating these credentials right now - nothing was rotated. Try again in a few seconds.'
            : err.status === 409
              ? 'License is not active.'
              : `Rotate failed (HTTP ${err.status}).`
          : 'Rotate failed.',
      );
    } finally {
      setEntBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <Link to="/licenses" className="text-sm text-brand-700 hover:underline">
          &larr; Back to licenses
        </Link>
      </div>

      {error && (
        <div className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error.message}
        </div>
      )}

      {!license && !error && <p className="text-sm text-gray-500">Loading…</p>}

      {license && (
        <>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold tracking-tight">{license.display_name}</h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 capitalize">
                {license.tier} tier
                {license.air_gapped ? ' · air-gapped' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill status={license.status} />
              {license.status === 'active' && (
                <button
                  type="button"
                  onClick={() => setRevoking(true)}
                  className="rounded border border-red-300 dark:border-red-700 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  Revoke
                </button>
              )}
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 sm:grid-cols-2">
            <Field label="Key prefix" value={`${license.license_key_prefix}…`} mono />
            <Field
              label="Instances"
              value={`${instances.length} linked / ${license.max_instances === null ? 'unlimited' : license.max_instances} cap`}
            />
            <Field label="Issued" value={fmtDateTime(license.issued_at)} />
            <Field
              label="Expires"
              value={license.expires_at ? fmtDateTime(license.expires_at) : 'Perpetual'}
            />
            <Field
              label="Features"
              value={license.features.length ? license.features.join(', ') : '—'}
            />
            <Field
              label="Subscription"
              value={license.subscription_id ?? '— (manual mint)'}
              mono={Boolean(license.subscription_id)}
            />
            {license.status === 'revoked' && (
              <>
                <Field
                  label="Revoked"
                  value={license.revoked_at ? fmtDateTime(license.revoked_at) : '—'}
                />
                <Field label="Revoke reason" value={license.revoked_reason ?? '—'} />
              </>
            )}
          </dl>

          {/* OCP-985 - agent lanes (Self-Host counts none by default), audited edit dialog. */}
          {license.agent_lanes && (
            <AgentLanesField
              target={{ kind: 'license', id: license.id }}
              lanes={license.agent_lanes}
              editable={license.status !== 'revoked'}
              onChanged={(next) => setLicense({ ...license, agent_lanes: next })}
            />
          )}

          {/* OCP-586 - what the license key delivered to each instance (never material). */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4" data-testid="license-entitlements">
            <h2 className="mb-1 text-sm font-semibold">Included entitlements (mail + AI)</h2>
            <p className="mb-2 text-xs text-gray-500">Provisioned when the instance exchanges the license key (POST /v1/license/entitlements). Revoke and rotate are the same calls the ocp_revoke_license_entitlement / ocp_rotate_license_entitlement tools make.</p>
            {entFlash && <div className="mb-2 text-xs text-gray-700 dark:text-gray-300">{entFlash}</div>}
            {rotated && (
              <div className="mb-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/30 p-2 text-xs">
                {rotated.mail && <div>Mail key: <code className="break-all">{rotated.mail}</code></div>}
                {rotated.ai && <div>AI key: <code className="break-all">{rotated.ai}</code></div>}
              </div>
            )}
            {entitlements.length === 0 && <p className="text-sm text-gray-500">No instance has exchanged this license key yet.</p>}
            {entitlements.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-1">Instance</th>
                    <th>Mail</th>
                    <th>AI</th>
                    <th>Rotations</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entitlements.map((e) => (
                    <tr key={e.id} className="border-t border-gray-100 dark:border-gray-800" data-testid={`entitlement-${e.instance_id}`}>
                      <td className="py-1 font-mono text-xs">{e.instance_id}{e.instance_url ? <div className="text-gray-500">{e.instance_url}</div> : null}</td>
                      <td className="text-xs">{e.mail.status}{e.mail.key_prefix ? ` ${e.mail.key_prefix}…` : ''}{e.mail.from ? <div className="text-gray-500">{e.mail.from}</div> : null}</td>
                      <td className="text-xs">{e.ai.status}{e.ai.key_prefix ? ` ${e.ai.key_prefix}…` : ''}</td>
                      <td className="text-xs">{e.rotations}{e.revoked_reason ? <div className="text-gray-500">{e.revoked_reason}</div> : null}</td>
                      <td className="text-right text-xs">
                        {license.status === 'active' && (e.mail.status === 'active' || e.ai.status === 'active') && (
                          <>
                            <button type="button" className="underline" disabled={entBusy === e.id} onClick={() => void rotateEntitlement(e, 'all')}>rotate keys</button>{' '}
                            <button type="button" className="text-red-600 underline" disabled={entBusy === e.id} onClick={() => void revokeEntitlement(e)}>revoke keys</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h2 className="mb-2 text-sm font-semibold">Linked instances</h2>
            {instances.length === 0 ? (
              <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
                No instance has verified against this license yet.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Instance</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">First seen</th>
                      <th className="px-3 py-2">Last seen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {instances.map((i) => (
                      <tr key={i.id}>
                        <td className="px-3 py-2 text-xs">
                          <div className="font-mono">{i.instance_id}</div>
                          {i.instance_url && (
                            <div className="text-gray-500">{i.instance_url}</div>
                          )}
                          {(typeof i.last_verify_meta?.hostname === 'string' || typeof i.last_verify_meta?.version === 'string') && (
                            <div className="text-gray-500" data-testid={`instance-meta-${i.instance_id}`}>
                              {typeof i.last_verify_meta?.hostname === 'string' ? `host ${i.last_verify_meta.hostname}` : ''}
                              {typeof i.last_verify_meta?.hostname === 'string' && typeof i.last_verify_meta?.version === 'string' ? ' - ' : ''}
                              {typeof i.last_verify_meta?.version === 'string' ? `core ${i.last_verify_meta.version}` : ''}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <InstancePill status={i.status} />
                        </td>
                        <td className="px-3 py-2 text-xs tabular-nums text-gray-500">
                          {fmtDateTime(i.first_seen_at)}
                        </td>
                        <td className="px-3 py-2 text-xs tabular-nums text-gray-500">
                          {fmtDateTime(i.last_seen_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {revoking && license && (
        <RevokeModal
          license={license}
          onCancel={() => setRevoking(false)}
          onDone={async () => {
            setRevoking(false);
            await load();
          }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }: { status: LicenseStatus }) {
  const cfg = {
    active: { label: 'active', cls: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' },
    revoked: { label: 'revoked', cls: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
    expired: { label: 'expired', cls: 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400' },
  }[status];
  return <span className={`rounded border px-2 py-0.5 text-xs ${cfg.cls}`}>{cfg.label}</span>;
}

function InstancePill({ status }: { status: InstanceStatus }) {
  const cfg = {
    active: { label: 'active', cls: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' },
    stale: { label: 'stale', cls: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200' },
    blocked: { label: 'blocked', cls: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
  }[status];
  return <span className={`rounded border px-2 py-0.5 text-xs ${cfg.cls}`}>{cfg.label}</span>;
}

function RevokeModal({
  license,
  onCancel,
  onDone,
}: {
  license: License;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (submitting) return;
    if (!reason.trim()) {
      setError('A reason is required (it lands in the audit log).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api(`/v1/admin/licenses/${license.id}/revoke`, {
        method: 'POST',
        body: { reason: reason.trim() },
      });
      await onDone();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 409
            ? 'Already revoked.'
            : `HTTP ${err.status}`
          : 'Failed.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Revoke license"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">Revoke license</h2>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Revokes <span className="font-medium">{license.display_name}</span>. Every
          linked instance is blocked on its next license-authority call. This cannot
          be undone.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Reason (audit trail), e.g. refund / contract ended / abuse"
          className="mt-3 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
        />
        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => void submit()}
            className="rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Revoking…' : 'Revoke'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`mt-0.5 text-sm text-gray-900 dark:text-gray-100 ${mono ? 'font-mono break-all' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

